import React, { useEffect, useRef, useState } from "react";
import Button from "../components/ui/Button";
import { ChevronDown, ChevronUp } from "lucide-react";
import './CheckoutSimplified.scss';
import PromoCommentForm from "../components/promo_codes/PromoCommentForm";
import { useDispatch, useSelector } from "react-redux";
import { addToCart } from "../store/cartSlice";
import { calculateSubtotal, calculateTotal, formatDisplayPrice, formatPrice, calculateRawTotal } from "../utils/priceHandler";
import { SHIPMENT_PRICE as shippingRate } from "../config/config";
import { resetCustomizedProduct, resetCustomizer, selectUploadedImageIds, setCustomizerStep } from "../store/customizerSlice";
import { getImageFromDB, resetIndexedDB, saveImageToDB } from "../utils/indexedDB";
import { useAffiliate } from "../hooks/useAffiliate";
import { useSearchParams } from "react-router";
import { fbqTrack } from "../utils/eventsTracking/fbq";
import { gtagEvent } from "../utils/eventsTracking/gtag";
import { trackCustomEvent } from "../utils/eventsTracking/trackCustomEvent";
import { Oval } from "react-loader-spinner";
import CheckoutFormSimplified from "../components/checkout/CheckoutFormSimplified";
import CartItemSimplified from "../components/cart/CartItemSimplified";
import PromoCodeField from "../components/promo_codes/PromoCodeField";
import { restoreCartFromRecovery } from "../utils/cartRecovery";
import { useGetProductsQuery, useRestoreCartQuery } from "../store/apiSlice";
import StripeCheckoutWrapper from "../components/checkout/StripeCheckoutWrapper";
import PaymentStep from "../components/checkout/PaymentStep";
import ExpressCheckout from "../components/checkout/ExpressCheckout";
import { bundleAwareCartItems } from "../utils/bundleAwareCartItems";
import BundleCheckoutItem from "../components/cart/BundleCheckoutItem";


const CheckoutPageSimplified = ({ isTablet, handleApplyPromoCode, promoError, promoCode, setPromoCode, isSubmittingPromo }) => {
  const dispatch = useDispatch();
  const [comment, setComment] = useState("");
  const cartItems = useSelector((state) => state.cart.cartItems);
  const transformedCartItems = bundleAwareCartItems(cartItems);
  const colorMode = useSelector((state) => state.ui.colorMode);
  const submittedPromoCodes = useSelector(state => state.cart.submittedPromoCodes);

  const { affiliate, isLoading } = useAffiliate();
  const customizedProduct = useSelector((state) => state.customizer.customizedProduct);
  const canvasRef = useRef(null);
  const uploadedImageIds = useSelector(selectUploadedImageIds);
  const hasGeneratedRef = useRef(false);
  const [isOrderTopSummaryExpanded, setIsOrderTopSummaryExpanded] = useState(false);
  const orderTopSummaryRef = useRef(null);
  const [isOrderBottomSummaryExpanded, setIsOrderBottomSummaryExpanded] = useState(false);
  const orderBottomSummaryRef = useRef(null);

  const formikRef = useRef();
  const [isFormCompleted, setIsFormCompleted] = useState(false);
  const [billingDetails, setBillingDetails] = useState({});
  const subtotal = calculateSubtotal(cartItems);
  const { discountedTotal, discountedTotalCents } = calculateTotal(subtotal, submittedPromoCodes);
  const originalTotal = calculateRawTotal(cartItems, submittedPromoCodes);
  const [checkoutError, setCheckoutError] = useState([]);
  const [clientSecret, setClientSecret] = useState(null);
  const [isSubmittingCheckout, setIsSubmittingCheckout] = useState(false);
  const [intentId, setIntentId] = useState(null);
  const [isUpdatingPaymentIntent, setIsUpdatingPaymentIntent] = useState(false);
  const [stripeCustomerId, setStripeCustomerId] = useState(null);

  // Restore cart if cart_id attribute is present
  const [searchParams] = useSearchParams();
  const cartId = searchParams.get('cart_id');
  const { data: products, isLoading: isLoadingProducts, isError: isErrorProducts } = useGetProductsQuery();
  const { data: restoredCartData, isLoading: isLoadingCartData, isError: isErrorResoringCart } = useRestoreCartQuery(cartId, {
    skip: !cartId,
  });
  const [restoringCart, setRestoringCart] = useState(false);


  useEffect(() => {
    if (
      restoredCartData && !isLoadingCartData && !isErrorResoringCart &&
      products && !isLoadingProducts && !isErrorProducts
    ) {
      const restore = async () => {
        setRestoringCart(true);
        await restoreCartFromRecovery(restoredCartData, dispatch, products, cartItems);
        setRestoringCart(false);
      };
      restore();
    }
  }, [
    restoredCartData,
    isLoadingCartData,
    isErrorResoringCart,
    products,
    isLoadingProducts,
    isErrorProducts,
  ]);

  //Reset customizer states
  useEffect(() => {
    if (!customizedProduct) {
      localStorage.removeItem("petType");
      localStorage.removeItem("petName");
      localStorage.removeItem("imageMetadata");
      localStorage.removeItem("label_template");
      resetIndexedDB("uploadedImages", cartItems);
      resetIndexedDB("resultImages", cartItems);
      resetIndexedDB("onProductImages", cartItems);
      dispatch(setCustomizerStep(1));
      dispatch(resetCustomizer());
    }
  }, [customizedProduct]);


  const hasFreeShippingPromo = () => {
    return submittedPromoCodes.some(promo => promo.free_shipping === true);
  };

  useEffect(() => {
    if (isTablet && isOrderBottomSummaryExpanded && orderBottomSummaryRef.current) {
      const section = orderBottomSummaryRef.current;
      section.style.height = section.scrollHeight + 'px';
    }
  }, [isOrderBottomSummaryExpanded, isTablet]);


  // Events for marketing
  useEffect(() => {
    if (isLoading) return;
    trackCustomEvent('checkout_visit', {
      subtotal: subtotal,
      products: cartItems.map(item => ({
        sku: item.sku,
        price: item.price,
        title: item.name,
        quantity: item.quantity,
        flavor: item.tag || null,
        subscription: item.subscription || false,
      })),
    }, affiliate?.code || null);
  }, [affiliate, isLoading]);

  useEffect(() => {
    fbqTrack('InitiateCheckout', {
      value: subtotal,
      currency: 'USD',
      num_items: cartItems.length
    });
    gtagEvent('begin_checkout', {
      value: subtotal,
      currency: 'USD',
      items: cartItems.map(item => ({
        item_id: item.sku,
        item_name: item.name,
        quantity: item.quantity,
        price: item.price,
        subscription: item.subscription || false,
        flavor: item.tag || null
      })),
    });
  }, []);

  useEffect(() => {
    if (!customizedProduct || !canvasRef.current || hasGeneratedRef.current) return;

    const generateAndAddCustomizedProduct = async () => {
      hasGeneratedRef.current = true;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const { product, isSubscribed, quantity } = customizedProduct;

      const resultImageId = localStorage.getItem("resultImageId");
      const resultImageBlob = await getImageFromDB("resultImages", resultImageId);

      if (!resultImageBlob) return;

      const onProductImage = await getImageFromDB("onProductImages", null, {
        productId: product.id,
        resultImageId,
      });

      if (onProductImage) {
        dispatch(addToCart({
          ...customizedProduct.product,
          quantity: customizedProduct.quantity,
          subscription: customizedProduct.isSubscribed,
          imageId: resultImageId,
          uploadedImageIds,
        }));
        dispatch(resetCustomizedProduct());
        return;
      }

      const mockupImage = new Image();
      mockupImage.src = product.image;
      mockupImage.crossOrigin = "anonymous";

      await new Promise((resolve) => {
        mockupImage.onload = () => {
          const customizedImage = new Image();
          customizedImage.src = URL.createObjectURL(resultImageBlob);
          customizedImage.crossOrigin = "anonymous";

          customizedImage.onload = () => {
            const canvasWidth = mockupImage.width;
            const canvasHeight = mockupImage.height;
            canvas.width = canvasWidth;
            canvas.height = canvasHeight;

            ctx.clearRect(0, 0, canvasWidth, canvasHeight);
            ctx.globalCompositeOperation = "source-over";
            ctx.drawImage(mockupImage, 0, 0, canvasWidth, canvasHeight);

            const overlayWidth = canvasWidth * 0.36;
            const overlayHeight = (overlayWidth * customizedImage.height) / customizedImage.width;
            const overlayX = (canvasWidth - overlayWidth) / 2;
            const overlayY = (canvasHeight - overlayHeight) / 1.65;

            ctx.drawImage(customizedImage, overlayX, overlayY, overlayWidth, overlayHeight);

            canvas.toBlob(async (finalBlob) => {
              if (!finalBlob) return resolve();

              await saveImageToDB(
                "onProductImages",
                `${product.id}_${resultImageId}`,
                finalBlob,
                product.id,
                resultImageId
              );

              dispatch(addToCart({
                ...customizedProduct.product,
                quantity: customizedProduct.quantity,
                subscription: customizedProduct.isSubscribed,
                imageId: resultImageId,
                uploadedImageIds,
              }));
              dispatch(resetCustomizedProduct());

              resolve();
            }, "image/png");
          };
        };
      });
    };

    generateAndAddCustomizedProduct();
  }, [customizedProduct]);



  const getStripeIntent = async () => {
    if (transformedCartItems.length === 0) return;
    setCheckoutError([]);
    try {
      setIsUpdatingPaymentIntent(true);
      const response = await fetch('https://api.treatink.com/functions/v1/create-stripe-intent', {
        method: 'POST',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: transformedCartItems,
          promoCodes: submittedPromoCodes.map((p) => p.code),
          clientTotal: discountedTotal
        }),
      });

      const data = await response.json();

      if (!data.client_secret) {
        throw new Error("No client_secret received from server");
      }
      setClientSecret(data.client_secret);
      setIntentId(data.intent_id);
    } catch (err) {
      setCheckoutError([err.message || "Could not initialize payment."]);
    } finally {
      setIsUpdatingPaymentIntent(false);
    }
  }

  useEffect(() => {
    getStripeIntent();
  }, [discountedTotal, submittedPromoCodes, cartItems]);

  const updateStripeIntent = async () => {
    if (!intentId) return;
    try {
      setIsUpdatingPaymentIntent(true);

      const response = await fetch('https://api.treatink.com/functions/v1/stripe-update-intent', {
        method: 'POST',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          intent_id: intentId,
          billingDetails,
          customer_id: stripeCustomerId || null
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to update payment intent");
      }

      setStripeCustomerId(data.customer_id);
      setIntentId(data.intent_id);
      return data.customer_id;
    } catch (err) {
      setCheckoutError([err.message || "Could not update payment."]);
    } finally {
      setIsUpdatingPaymentIntent(false);
    }
  };

  const renderOrderSummaryContent = (showCartTotals, showPromoCodeForm) => (
    <>
      {renderCartItems()}
      {showPromoCodeForm &&
        <PromoCommentForm
          promoCode={promoCode}
          setPromoCode={setPromoCode}
          comment={comment}
          setComment={setComment}
          isSubmitting={isSubmittingPromo}
          promoError={promoError}
          handleApplyPromoCode={handleApplyPromoCode}
          isMobile={isTablet}
          simplifiedVersion={true}
        />
      }
      {showCartTotals && renderCartTotals()}
    </>
  );

  const renderCartItems = () => (
    customizedProduct && transformedCartItems.length === 0 ? (
      <div className="flex justify-center items-center w-full h-[300px]">
        <Oval
          visible={true}
          height={50}
          width={50}
          color="#a99cdf"
          strokeWidth="3"
          strokeWidthSecondary="3"
          secondaryColor="#e2e6ff"
          ariaLabel="oval-loading"
        />
      </div>
    ) : (
      transformedCartItems.map((item) => (
        item.type === 'bundle' ? (
          <BundleCheckoutItem key={item.cartId} bundle={item} />
        ) : (
          <CartItemSimplified key={item.cartId} item={item} />
        )
      ))
    )
  );

  const renderCartTotals = (swapColorsOnDarkMode) => (
    <div className={`summary-totals gap-3 md:gap-4 mt-4 flex flex-col ${colorMode === 'dark' && swapColorsOnDarkMode ? 'color-swap' : ''}`}>
      <div className="flex justify-between items-end">
        <span>Subtotal</span>
        <span className="text-mid">{formatDisplayPrice(subtotal)}</span>
      </div>
      <div className="flex justify-between items-end">
        <span>Shipping</span>
        <span className="text-mid">
          {formatDisplayPrice(hasFreeShippingPromo() ? 0 : shippingRate)}
        </span>
      </div>
      <div className="flex justify-between items-end form-title body-heading h4">
        <span>Total</span>
        <span className="flex flex-col-reverse items-end color-primary">
          {formatDisplayPrice(discountedTotal)}
          {discountedTotal !== originalTotal && (
            <span className="ml-2 text-sm font-normal text-gray-500 line-through">
              {formatDisplayPrice(originalTotal)}
            </span>
          )}
        </span>
      </div>
    </div>
  );


  const renderOrderSummary = (section, isExpanded, setExpanded, summarySectionRef) => (
    <div>
      {isTablet && (
        <div
          className="expand-order-summary flex items-center justify-between h-[80px] px-[20px] gap-2 w-full cursor-pointer rounded-t-20"
          onClick={() => setExpanded(!isExpanded)}
        >
          {section === 'top' ?
            <>
              <div className="flex gap-3 items-center">
                <span className="text-base font-medium">Order Summary</span>
                {isOrderTopSummaryExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>

              <div className="form-title body-heading h4">
                <span className="flex flex-col-reverse items-end color-primary">
                  {formatDisplayPrice(discountedTotal)}
                  {discountedTotal !== originalTotal && (
                    <span className="ml-2 text-sm font-normal text-gray-500 line-through">
                      {formatDisplayPrice(originalTotal)}
                    </span>
                  )}
                </span>
              </div>
            </>
            :
            <>
              <h5 className="form-title body-heading text-left mb-0">Order Summary</h5>
              <div className="flex gap-3 items-center">
                <span className="text-base font-medium">{!isExpanded ? 'Show' : 'Hide'}</span>
                {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </>
          }
        </div>
      )}
      <div className={`order-summary relative ${isTablet ? 'mobile' : ''}`}>
        <div className="order-summary-inner rounded-20">
          {isTablet &&
            <div
              ref={summarySectionRef}
              className="collapsible-content"
              style={{
                height: isExpanded
                  ? `${summarySectionRef.current?.scrollHeight || 0}px`
                  : '0px',
              }}
            >
              <div className={`${isTablet ? 'pt-5' : 'pt-7'} pb-4 px-1`}>
                {renderOrderSummaryContent(section === 'top', section === 'top')}
              </div>
            </div>
          }
        </div>
      </div>

      {section === 'bottom' &&
        <div className="pt-4 px-[20px]">
          <PromoCodeField
            promoCode={promoCode}
            setPromoCode={setPromoCode}
            isSubmitting={isSubmittingPromo}
            promoError={promoError}
            handleApplyPromoCode={handleApplyPromoCode}
            simplifiedVersion={true}
            submittedPromoCodes={submittedPromoCodes}
          />
          {renderCartTotals(true)}
        </div>
      }
    </div>
  );


  if (cartId && (isLoadingCartData || restoringCart)) {
    return (
      <div className="flex justify-center items-center w-full h-screen min-h-screen">
        <Oval
          visible={true}
          height={50}
          width={50}
          color="#a99cdf"
          strokeWidth="3"
          strokeWidthSecondary="3"
          secondaryColor="#e2e6ff"
          ariaLabel="oval-loading"
        />
      </div>
    );
  }


  return (
    <>
      {!customizedProduct && cartItems.length === 0
        ?
        <section className="checkout-simplified-page min-h-[50vh] full-width flex flex-col gap-10 items-center justify-center">
          <p className="color-swap text-mid">Your cart is empty.</p>
          <div>
            <Button type="link" link="/customizer" variant="filled">
              Start Personalizing
            </Button>
          </div>
        </section>
        :
        <section className="checkout-simplified-page  w-full flex flex-col gap-0 md:gap-10 items-center p-0">
          <div className="checkout-container w-full gap-0 md:gap-5">
            {isTablet && renderOrderSummary('top', isOrderTopSummaryExpanded, setIsOrderTopSummaryExpanded, orderTopSummaryRef)}
            {isTablet && affiliate && (
              <div className="flex justify-center w-full">
                <img
                  src={affiliate.logo}
                  alt={`${affiliate.name} logo`}
                  className="affiliate-logo mt-6"
                />
              </div>
            )}

            <div className="checkout-form-container flex flex-col gap-5 md:gap-10">
              <ExpressCheckout
                comment={comment}
                setIsSubmittingCheckout={setIsSubmittingCheckout}
                setCheckoutError={setCheckoutError}
                discountedTotal={discountedTotal}
                discountedTotalCents={discountedTotalCents}
                affiliate={affiliate}
                formikRef={formikRef}
              />
              <CheckoutFormSimplified
                clientSecret={clientSecret}
                setClientSecret={setClientSecret}
                setCheckoutError={setCheckoutError}
                checkoutError={checkoutError}
                shippingRate={shippingRate}
                promoCode={promoCode}
                handleApplyPromoCode={handleApplyPromoCode}
                isMobile={isTablet}
                cartItems={cartItems}
                setComment={setComment}
                formikRef={formikRef}
                getStripeIntent={getStripeIntent}
                setBillingDetails={setBillingDetails}
                setIsFormCompleted={setIsFormCompleted}
              />
              <StripeCheckoutWrapper clientSecret={clientSecret}>
                <div id="payment-container">
                  <PaymentStep
                    formikRef={formikRef}
                    isSubmittingCheckout={isSubmittingCheckout}
                    setIsSubmittingCheckout={setIsSubmittingCheckout}
                    comment={comment}
                    affiliate={affiliate}
                    isUpdatingPaymentIntent={isUpdatingPaymentIntent}
                    setCheckoutError={setCheckoutError}
                    clientSecret={clientSecret}
                    isTablet={isTablet}
                    updateStripeIntent={updateStripeIntent}
                    isFormCompleted={isFormCompleted}
                    showOrderSummaryFunction={() =>
                      isTablet && renderOrderSummary(
                        'bottom',
                        isOrderBottomSummaryExpanded,
                        setIsOrderBottomSummaryExpanded,
                        orderBottomSummaryRef
                      )
                    }
                  />
                </div>
              </StripeCheckoutWrapper>
              {checkoutError.length > 0 && (
                <div className="text-red-500 mt-2 px-[20px]">
                  {checkoutError.map((msg, index) => (
                    <p key={index}>{msg}</p>
                  ))}
                </div>
              )}
            </div>

            {!isTablet &&
              <div className="order-summary">
                <div className="order-summary-inner rounded-20">
                  {affiliate && (
                    <div className="flex justify-center w-full">
                      <img
                        src={affiliate.logo}
                        alt={`${affiliate.name} logo`}
                        className="affiliate-logo mb-6"
                      />
                    </div>
                  )}
                  {renderCartItems()}
                  <PromoCommentForm
                    promoCode={promoCode}
                    setPromoCode={setPromoCode}
                    comment={comment}
                    setComment={setComment}
                    isSubmitting={isSubmittingPromo}
                    promoError={promoError}
                    handleApplyPromoCode={handleApplyPromoCode}
                    isMobile={isTablet}
                    simplifiedVersion={true}
                  />
                  <div className={`flex ${isTablet ? 'flex-col-reverse' : 'flex-col'}`}>
                    <div className="summary-totals gap-3 md:gap-4 mt-0 md:mt-8 flex flex-col">
                      <div className="flex justify-between items-end"><span>Subtotal</span><span className="text-mid">{formatDisplayPrice(subtotal)}</span></div>
                      <div className="flex justify-between items-end"><span>Shipping</span><span className="text-mid">{formatDisplayPrice(hasFreeShippingPromo() ? 0 : shippingRate)}</span></div>
                      <div className="flex justify-between items-end form-title body-heading h4">
                        <span>Total</span>
                        <span className="flex flex-col-reverse items-end color-primary">
                          {formatDisplayPrice(discountedTotal)}
                          {discountedTotal !== originalTotal && (
                            <span className="ml-2 text-sm font-normal text-gray-500 line-through">
                              {formatDisplayPrice(originalTotal)}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>
        </section >
      }
      <canvas ref={canvasRef} className="hidden" />
    </>
  );
};

export default CheckoutPageSimplified;
